import {
  PropsWithChildren,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
} from 'react';
import { useLocation } from 'react-router-dom';

import { Permissions } from 'interfaces/Permissions';
import { ApiResponse, api } from '@services/api';
import MeAPICaller from '@services/api/me';
import { isEmpty } from 'utils/object';

type EstablishmentPermission = {
  context: string;
  resource?: string;
  establishmentUUID?: string;
  unitUUID?: string;
};

export type TPermissionContext = {
  permissions: Permissions;
  loading: boolean;
  hasPermission: (context: string, resource?: string) => boolean;
  handleDynamicPermission: ({
    context,
    resource,
    establishmentUUID,
    unitUUID,
  }: EstablishmentPermission) => boolean;
  refetch: () => Promise<void | ApiResponse>;
};

export const PermissionContext = createContext<TPermissionContext>({
  permissions: {},
  loading: false,
  hasPermission: () => true,
  handleDynamicPermission: () => true,
  refetch: () => Promise.resolve(),
});

type Props = {
  context?: TPermissionContext;
};

export function PermissionProvider({
  children,
  context,
}: PropsWithChildren<Props>) {
  const [permissions, setPermissions] = useState<Permissions>({});
  const [admin, setAdmin] = useState(false);
  const [loading, setLoading] = useState(true);

  const location = useLocation();

  const authorization = api.defaults.headers.common.Authorization;

  const handleRefetchProfile = useCallback(async () => {
    if (!authorization) return;

    const { admin, accessGroup } = await MeAPICaller.fetchMe();
    let permissions: Permissions = accessGroup?.permissions ?? {};

    if (permissions) {
      setPermissions(permissions);
    }

    setAdmin(Boolean(admin));
  }, [authorization]);

  useEffect(() => {
    handleRefetchProfile().then(() => setLoading(false));
  }, [authorization, handleRefetchProfile, location]);

  const handleHasPermission = (context: string, resource: string = 'read') => {
    if (admin) return true;
    return validatePermission(context, resource);
  };

  const validatePermission = (context: string, resource: string = 'read') => {
    if (!permissions || isEmpty(permissions)) return false;

    const hasContext = permissions[context];
    if (!hasContext) return false;

    return !!permissions[context][resource];
  };

  const contextValue = context ?? {
    permissions,
    loading,
    refetch: handleRefetchProfile,
    hasPermission: handleHasPermission,
  };

  return (
    <PermissionContext.Provider value={contextValue as TPermissionContext}>
      {children}
    </PermissionContext.Provider>
  );
}

export function usePermissionContext() {
  return useContext(PermissionContext);
}
